/**
 * heroFx.ts — ヒーロー装飾アニメーション
 *
 * ① 装飾ライン（stroke-dashoffset ドロー）
 * ② 「爆速」アンダーライン（stroke-dashoffset ドロー）
 * ③ 破線リング（gsap 連続回転）
 * ④ アクセントドット（ゆるやかドリフト）
 *
 * - prefers-reduced-motion: reduce → アニメーション全スキップ
 *   SVG のデフォルト（dashoffset なし）= 描き切り状態 → 静止表示になる
 * - JS 無効時: SVG 初期状態（dashoffset 未設定）= 描き切り → 装飾は見える
 * - DrawSVG プラグイン不使用（GSAP 3.x 無料機能のみ）
 * - catch h1 / copy テキストには一切触れない（LCP 保護）
 */
import { gsap } from "gsap";

function initHeroFx(): void {
  // ─── prefers-reduced-motion ガード ─────────────────────────
  // SVG のデフォルトは描き切り状態のため、スキップするだけで静止表示になる
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  // ─── ① 装飾ライン 連続フロー（水流型・yoyo 廃止） ──────────────
  const decoPaths = document.querySelectorAll<SVGPathElement>(
    ".p-hero__deco path"
  );

  // 各パスのダッシュ設定（dash長・gap長・流れ速度）
  // dash:gap 比を高く保つことで「ほぼ実線に見えつつ静かに流れる」上品な演出にする
  const lineConfigs: Array<{ dash: number; gap: number; duration: number }> = [
    { dash: 300, gap: 78,  duration: 6   }, // ①: sky→primary（長いS字）
    { dash: 260, gap: 65,  duration: 7   }, // ②: sky→soft-green（大きなスイープ）
    { dash: 175, gap: 44,  duration: 4.5 }, // ③: peach→yellow（短い弧）
  ];

  decoPaths.forEach((path, i) => {
    const cfg = lineConfigs[i] ?? { dash: 200, gap: 50, duration: 5.5 };
    const period = cfg.dash + cfg.gap;

    // HTML 属性の opacity を読んで退避（gsap.set で上書きするため）
    const targetOpacity = parseFloat(path.getAttribute("opacity") ?? "0.5");

    // JS で dasharray・dashoffset を設定（JS 未実行時は属性なし = 実線で静止表示）
    gsap.set(path, {
      strokeDasharray: `${cfg.dash} ${cfg.gap}`,
      strokeDashoffset: 0,
      opacity: 0,
    });

    // ロード時: やわらかなフェードイン（ラインが自然に浮かび上がる）
    gsap.to(path, {
      opacity: targetOpacity,
      duration: 1.4,
      delay: 0.4 + i * 0.3,
      ease: "power2.out",
    });

    // 連続フロー: dashoffset を 1 周期ぶん動かし続ける → 視覚的に完全ループ
    gsap.to(path, {
      strokeDashoffset: `-=${period}`,
      duration: cfg.duration,
      delay: 0.4 + i * 0.3,
      ease: "none",
      repeat: -1,
    });
  });

  // ─── ② 「爆速」アンダーライン ドロー ──────────────────────
  const underlinePath = document.querySelector<SVGPathElement>(
    ".p-hero__underline path"
  );
  if (underlinePath) {
    const len = underlinePath.getTotalLength();
    gsap.set(underlinePath, { strokeDasharray: len, strokeDashoffset: len });
    gsap.to(underlinePath, {
      strokeDashoffset: 0,
      duration: 0.6,
      delay: 0.5,
      ease: "power2.out",
    });
  }

  // ─── ③ 破線リング 連続回転 ────────────────────────────────
  const ring = document.querySelector<SVGElement>(".p-hero__ring");
  if (ring) {
    gsap.to(ring, {
      rotation: 360,
      duration: 7, // もっとクルクル（24s→7s）
      ease: "none",
      repeat: -1,
      transformOrigin: "50% 50%",
    });
  }

  // ─── ④ アクセントドット ゆるやかドリフト ──────────────────
  const dots = document.querySelectorAll<SVGCircleElement>(
    ".p-hero__deco circle[data-drift]"
  );
  dots.forEach((dot, i) => {
    gsap.to(dot, {
      y: i % 2 === 0 ? -10 : 8,
      duration: 3.2 + i * 0.7,
      delay: i * 0.5,
      ease: "sine.inOut",
      yoyo: true,
      repeat: -1,
    });
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHeroFx);
} else {
  initHeroFx();
}
